import { useApiQuery } from "@/api";
import { HydratedMetric } from "@/features/Metrics/HydratedMetric";
import { getLineagedMetric } from "@/features/Metrics/Metrics.queries";
import { asOfDateAtom } from "@/features/Metrics/state/Metrics.state";
import { useAtomValue, useSetAtom } from "jotai";
import { EmptyStateCard } from "../../components/EmptyStateCard";
import { MetricLineage } from "@/features/Metrics/components/MetricLineage";
import { AIMContextViewAtom } from "../AIMPanelContext";
import { Period } from "@/features/Metrics/Period";
import RightSidebarButton from "@/features/Navigation/components/RightSidebarButton";
import clsx from "clsx";
import { MetricInfoDisplay } from "@/features/Metrics/components/MetricInfoDisplay";

export function MetricLineagePanel({
    metric,
    period,
    title,
}: {
    metric: HydratedMetric | null;
    title?: string;
    period: Period;
}) {
    const setAIMContextView = useSetAtom(AIMContextViewAtom);

    if (!metric)
        return (
            <EmptyStateCard
                heading="Internal Error - Metric not found"
                body="Unable to show lineage for this metric."
            />
        );

    return (
        <div
            className={clsx(
                "px-6 pb-6",
                "custom-scrollbar absolute inset-0 flex min-h-0 flex-col overflow-hidden",
            )}
        >
            <div className="sticky my-4 flex flex-col">
                <RightSidebarButton
                    onClick={() => {
                        setAIMContextView(null);
                    }}
                />
                {metric && (
                    <>
                        <h2 className="pageheading ">
                            {title ||
                                `${metric.label} - ${period.getFullName(metric.resolution)}`}
                        </h2>
                        <MetricInfoDisplay metric={metric.rawData} />
                    </>
                )}
            </div>
            <div className="custom-scrollbar hide-scrollbar overflow-y-scroll">
                {metric && (
                    <MetricLineagePanelContent
                        metric={metric}
                        period={period}
                    />
                )}
            </div>
        </div>
    );
}

export function MetricLineagePanelContent({
    metric,
    period,
}: {
    metric: HydratedMetric;
    period: Period;
}) {
    const asOfDate = useAtomValue(asOfDateAtom);
    const setAIMContextView = useSetAtom(AIMContextViewAtom);
    const { data, isLoading, isError } = useApiQuery(getLineagedMetric, {
        params: {
            code: metric.code,
            resolution: metric.resolution,
            toDate: asOfDate.format("YYYY-MM-DD"),
            period,
        },
    });

    if (isLoading && !data && !isError) {
        return <div>Loading...</div>;
    }

    if (!data || isError) {
        return (
            <EmptyStateCard
                heading="Data for this field is missing or unavailable."
                body="Please contact your admin or company representative for more information."
            />
        );
    }

    return (
        <MetricLineage
            metric={data}
            onSourceSelect={() => {
                setAIMContextView(null);
            }}
        />
    );
}
